import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';
import { CSSTransition } from 'react-transition-group';

import { Portal } from '@components/Portal';

import styles from './Modal.module.scss';
import { SvgIcon, SvgIconSrc } from '../SvgIcon';

export const ModalComponent = ({
  isVisible,
  backdrop = true,
  children,
  customStyles,
  className,
  flowBackdrop = false,
  renderCustomCross,
  onClose,
  hideCloseButton = false,
  disableEscapeKey = false,
  disableBackdropClick = false,
  /** When true, hides the X, blocks backdrop and Escape close (e.g. while a form is submitting). */
  preventClose = false,
  customBackdropStyle = {},
}) => {
  const backdropRef = useRef(null);

  const effectiveHideCloseButton = hideCloseButton || preventClose;
  const effectiveDisableBackdropClick = disableBackdropClick || preventClose;
  const effectiveDisableEscapeKey = disableEscapeKey || preventClose;

  // Only handle backdrop click if backdrop is enabled
  const handleBackdropClick = (e) => {
    if (!backdrop) return; // Early return if backdrop is disabled
    if (e.target === backdropRef.current && !effectiveDisableBackdropClick) {
      onClose();
    }
  };

  // Handle ESC key only if not disabled
  useEffect(() => {
    if (!isVisible || effectiveDisableEscapeKey) return;

    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey, true);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey, true);
    };
  }, [isVisible, effectiveDisableEscapeKey, onClose]);

  const modalClass = classNames(styles.modal, className);
  const backdropClass = classNames(styles.backdrop, {
    [styles.backdrop_enabled]: backdrop,
    [styles.flow_backdrop_enabled]: flowBackdrop,
  });

  const cross = renderCustomCross ? (
    renderCustomCross(onClose)
  ) : !effectiveHideCloseButton ? (
    <div className={styles.cross} onClick={onClose}>
      <SvgIcon src={SvgIconSrc.cross} size={12} />
    </div>
  ) : null;

  return (
    <Portal>
      <CSSTransition
        in={isVisible}
        classNames="modalTransition"
        timeout={300}
        unmountOnExit
      >
        <div
          className={backdropClass}
          onClick={handleBackdropClick}
          ref={backdropRef}
          style={customBackdropStyle}
        >
          <div className={styles.modalContent}>
            <div className={modalClass}>
              {/* <Scrolling vertical className={modalClass} style={customStyles}> */}
              {cross}
              {children}
              {/* </Scrolling> */}
            </div>
          </div>
        </div>
      </CSSTransition>
    </Portal>
  );
};

ModalComponent.displayName = 'Modal';
